import React, { useRef, useCallback, useEffect } from 'react';
import { Bold, Italic, Underline, List, ListOrdered } from 'lucide-react';

const ToolbarBtn = ({ onMouseDown, title, children }) => (
  <button
    type="button"
    title={title}
    onMouseDown={onMouseDown}
    className="p-1.5 rounded hover:bg-slate-200 text-slate-600 transition-colors"
  >
    {children}
  </button>
);

export const RichTextEditor = ({ value, onChange, placeholder = 'Inserisci testo...', minHeight = '120px' }) => {
  const editorRef = useRef(null);
  const lastExternalValue = useRef(value);

  // Sync only when value changes externally (form reset), not from user typing
  useEffect(() => {
    if (!editorRef.current) return;
    if (value !== lastExternalValue.current) {
      lastExternalValue.current = value;
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  // Initialize on mount
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || '';
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = useCallback((command, val = null) => (e) => {
    e.preventDefault();
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    onChange?.(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const handleInput = useCallback(() => {
    const html = editorRef.current?.innerHTML || '';
    lastExternalValue.current = html;
    onChange?.(html);
  }, [onChange]);

  return (
    <div className="border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0">
      <div className="flex items-center gap-0.5 px-1.5 py-1 border-b bg-slate-50">
        <ToolbarBtn onMouseDown={exec('bold')} title="Grassetto">
          <Bold className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onMouseDown={exec('italic')} title="Corsivo">
          <Italic className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onMouseDown={exec('underline')} title="Sottolineato">
          <Underline className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <div className="w-px h-4 bg-slate-300 mx-1" />
        <ToolbarBtn onMouseDown={exec('insertUnorderedList')} title="Elenco puntato">
          <List className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onMouseDown={exec('insertOrderedList')} title="Elenco numerato">
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarBtn>
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        data-placeholder={placeholder}
        style={{ minHeight }}
        className="px-3 py-2 text-sm outline-none [&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-slate-400 [&:empty]:before:pointer-events-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_b]:font-bold [&_strong]:font-bold [&_i]:italic [&_em]:italic [&_u]:underline"
        suppressContentEditableWarning
      />
    </div>
  );
};
